
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PenLine, Globe, Plane, ArrowRight } from 'lucide-react';
import { BlogPostCard } from './BlogPostCard';
import { Link } from 'react-router-dom';

interface BlogSectionProps {
  hostId?: string;
  isOwnProfile?: boolean;
}

export const BlogSection = ({ hostId, isOwnProfile = false }: BlogSectionProps) => {
  const { data: blogPosts, isLoading, error } = useQuery({
    queryKey: ['blog-posts', hostId],
    queryFn: async () => {
      console.log('Starting blog posts fetch, hostId:', hostId);
      
      let query = supabase
        .from('community_posts')
        .select(`
          *,
          profiles:user_id (
            username,
            avatar_url
          )
        `)
        .eq('type', 'blog')
        .order('created_at', { ascending: false })
        .limit(3);

      if (hostId) {
        query = query.eq('user_id', hostId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching blog posts:', error);
        throw error;
      }
      
      console.log('Found blog posts:', data);
      return data || [];
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Travel Stories
          </h2>
          <div className="flex items-center gap-4">
            {isOwnProfile && (
              <Link to="/blog/edit">
                <Button variant="outline" className="gap-2">
                  <PenLine className="w-4 h-4" />
                  Share Your Journey
                </Button>
              </Link>
            )}
            {!hostId && (
              <Link to="/blogs">
                <Button variant="link" className="gap-2">
                  Explore All Stories
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
        
        <div className="grid gap-6">
          {blogPosts && blogPosts.length > 0 ? (
            blogPosts.map((post) => (
              <BlogPostCard 
                key={post.id} 
                post={{
                  ...post,
                  author: post.profiles
                }}
                isOwnPost={isOwnProfile && post.user_id === hostId}
              />
            ))
          ) : (
            <div className="text-center py-12 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-900 rounded-lg">
              <Plane className="w-16 h-16 text-primary mx-auto mb-4 animate-bounce" />
              <h3 className="text-xl font-semibold text-primary mb-2">No travel stories yet</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
                {isOwnProfile 
                  ? "Share your amazing travel experiences, tips, and discoveries with fellow adventurers"
                  : hostId 
                    ? "This host hasn't shared any travel stories yet"
                    : "No travel stories have been shared yet"}
              </p>
              {isOwnProfile && (
                <Link to="/blog/edit">
                  <Button variant="outline" className="gap-2">
                    <PenLine className="w-4 h-4" />
                    Begin Your Travel Blog
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
